import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Title, Text, Card, Badge, Group, Stack, Button,
  Tabs, Avatar, Progress, Divider, Skeleton, Drawer,
  ActionIcon, Tooltip, Loader, Center, Box, Menu, Indicator, ScrollArea
} from '@mantine/core';
import { IconArrowLeft, IconUsers, IconCalendar,
  IconBooks, IconNotes, IconUserCheck, IconPencil,
  IconRefresh, IconClipboardCheck, IconMessageQuestion,
  IconPlus, IconChevronDown, IconEye,

} from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService,
  useRegistrationStatusService,
  Registration,
  RegistrationListView,
  RegistrationStatus,
  ACCourseSessionModelView,
  RegistrationSessionModelView,
  RegistrationCourseCustomDataModelView
} from '@actschurch/ccms-sdk';
import CourseFilesTab from './Tabs/CourseFilesTab';
import NotesTab from './Tabs/NotesTab';
import MembersTab from './Tabs/MembersTab';
import FacilitatorsTab from './Tabs/FacilitatorsTab';
import EditTab from './Tabs/EditTab';
import AdditionalDetailsTab from './Tabs/AdditionalDetailsTab';
import ChildrenTab, { parseChildren } from '../../Components/ChildrenTab';
import { tabStyle } from '@/components/ui/TabStyle';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { useSessionTabs } from './Hooks/useSessionTabs';
import SpecialSessionsTabContent from './SpecialSession/SpecialSessionsTabContent';
import RegistrationSessionDetailsModal from './Components/RegistrationSessionDetailsModal';
import EditSessionDrawer from './Components/EditSessionDrawer';
import SessionStateMenu from './Components/SessionStateMenu';
import NotFoundState from '../Components/EmptyState';
import { useAuth } from '@/hooks/useEnrichedAuth';
import { IconBabyCarriage } from '@tabler/icons-react';

// Static tabs keep their fixed keys; dynamic session tabs use whatever
// key buildSessionTabs() produced ('sessions', or `special-<id>`).
type StaticTabKey = 'members' | 'facilitators' | 'additionalDetails' | 'notes' | 'courseFiles' ;
type TabKey = StaticTabKey | string;

export default function RegistrationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const registrationService = useRegistrationService();
  const statusService       = useRegistrationStatusService();

  const [registration, setRegistration] = useState<Registration | null>(null);
  const [listItem, setListItem] = useState<RegistrationListView | null>(null);
  const [childrenRow, setChildrenRow] = useState<RegistrationCourseCustomDataModelView | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('members');
  const [detailsSession, setDetailsSession] = useState<RegistrationSessionModelView | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<RegistrationSessionModelView | null>(null);
  const [editSessionOpen, setEditSessionOpen] = useState(false);
  const [statuses, setStatuses] = useState<RegistrationStatus[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const { user } = useAuth();

  const openSessionDetails = (session: RegistrationSessionModelView) => {
  setDetailsSession(session);
  setDetailsOpen(true);
};

const openSessionEdit = (session: RegistrationSessionModelView) => {
  setEditingSession(session);
  setEditSessionOpen(true);
};

  // ── Edit drawer ──────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);

  const registrationID = Number(id);

  // ── Dynamic session tabs (special sessions + one combined "Sessions" tab) ─
  // courseId comes from `registration`, which loads asynchronously — the hook
  // simply won't fetch the course catalogue (and orderNo lookup) until it's set.
  const {
    tabs: sessionTabs,
    loading: sessionTabsLoading,
    refetch: refetchSessionTabs,
    courseSessions,
    courseSessionsLoading,
    refetchCourseSessions,
  } = useSessionTabs(
    registrationID,
    registration?.courseID,
    true // fetched once on mount, independent of which tab is active
  );

  // ── Add Session (ported from old SessionsTab.tsx) ─────────────────────────
  const [addingSessionID, setAddingSessionID] = useState<number | null>(null);

  // Sessions from the course that are active and not already added to this registration.
  const availableCourseSessions = useMemo(() => {
    const addedIDs = new Set(
      sessionTabs.flatMap((tab) =>
        tab.kind === 'special' ? [tab.session.courseSessionID] : tab.sessions.map((s) => s.courseSessionID)
      )
    );
    return courseSessions.filter((cs) => cs.isActive && !addedIDs.has(cs.courseSessionID));
  }, [courseSessions, sessionTabs]);

  // All sessions across every tab (special + normal), for the combined "Sessions" tab.
  const allSessions = useMemo(() => {
    return sessionTabs.flatMap((tab) =>
      tab.kind === 'special' ? [tab.session] : tab.sessions
    );
  }, [sessionTabs]);

  const handleSessionUpdate = (updatedSession: RegistrationSessionModelView) => {
    void refetchSessionTabs();
    setDetailsSession((current) => (current?.registrationSessionID === updatedSession.registrationSessionID ? updatedSession : current));
    setEditingSession((current) => (current?.registrationSessionID === updatedSession.registrationSessionID ? updatedSession : current));
  };

  const handleAddFromCourseSession = async (courseSession: ACCourseSessionModelView) => {
    setAddingSessionID(courseSession.courseSessionID);
    const sessionName = courseSession.displayName ?? courseSession.name ?? 'Untitled Session';

    try {
      await registrationService.addSession(registrationID, {
        courseSessionID: courseSession.courseSessionID,
        name: sessionName,
        description: courseSession.description ?? '',
      });
      toast.success('Session Added', `"${sessionName}" has been added.`);
      await refetchSessionTabs();
      await refetchCourseSessions();
    } catch (e) {
      console.error('Error adding registration session:', e);
      toast.error('Error', 'Could not add session.');
    } finally {
      setAddingSessionID(null);
    }
  };

  // ── Fetch registration (raw record + list enrichment) ────────────────────
  const fetchRegistration = useCallback(async () => {
    if (!registrationID) return;
    setLoading(true);
    try {
      const [detailRes, listRes] = await Promise.all([
        registrationService.getById(registrationID),
        registrationService.listView(),
      ]);

      const detail: any = (detailRes as any)?.data ?? detailRes;
      setRegistration(detail ?? null);

      const items: RegistrationListView[] = (listRes as any)?.items ?? (listRes as any)?.data?.items ?? [];
      const match = items.find((r) => r.registrationID === registrationID) ?? null;
      setListItem(match);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load registration.');
    } finally {
      setLoading(false);
    }
  }, [registrationService, registrationID]);

  useEffect(() => { fetchRegistration(); }, [fetchRegistration]);


// ── Fetch registration statuses for the "Change Status" dropdown ─────────

    useEffect(() => {
      setLoadingStatuses(true);
      statusService.getAll()
        .then(setStatuses)
        .catch(() => toast.error('Error', 'Could not load registration statuses.'))
        .finally(() => setLoadingStatuses(false));
    }, [statusService]);

    const handleStatusChange = async (value: string | null) => {
      if (!value || !registration) return;
      const newStatusID = Number(value);
      if (newStatusID === registration.statusID) return;

      setChangingStatus(true);
      try {
        await registrationService.changeStatus(registrationID, newStatusID);
        toast.success('Status Updated', 'Registration status changed.');
        await fetchRegistration();
      } catch (e: any) {
        console.error('changeStatus failed:', e, e?.response?.data, e?.message);
        toast.error('Error', e?.message ?? 'Failed to change status.');
      } finally {
        setChangingStatus(false);
      }
    };

    //── Fetch children data from course custom data─────────

    const fetchChildrenData = useCallback(async () => {
  if (!registrationID) return;
  try {
    const res = await (registrationService as any).getCourseCustomData(registrationID);
    const data: RegistrationCourseCustomDataModelView[] = Array.isArray(res) ? res : res?.data ?? [];
    const found = data.find((r) => r.field?.fieldKey === 'children') ?? null;
    // A row with an empty or unparsable array counts as "no children" for tab visibility.
    setChildrenRow(found && parseChildren(found).length > 0 ? found : null);
  } catch (e) {
    console.error('Error fetching children data:', e);
    // Non-fatal — just hide the tab if this fails.
  }
}, [registrationService, registrationID]);

useEffect(() => { fetchChildrenData(); }, [fetchChildrenData]);

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading && !registration) {
    return (
      <Container size="xl" py="xl">
        <Skeleton height={36} width={220} mb="md" />
        <Skeleton height={140} radius="md" mb="md" />
        <Skeleton height={400} radius="md" />
      </Container>
    );
  }

  if (!registration) {
    return (
      <Container size="xl" py="xl">
        <NotFoundState entityLabel="registration" backTo="/Grow/Registration" />
      </Container>
    );
  }

  const statusColor = listItem?.effectiveStatusColor ?? listItem?.statusColor ?? undefined;
  const statusName  = listItem?.effectiveStatusName  ?? listItem?.statusName  ?? '—';
  const courseName  = listItem?.courseName ?? `Course ${registration.courseID}`;

  return (
    <Container size="xl" py="xl">
      {/* ── Back navigation ── */}
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate('/Grow/Registration')}
        mb="md"
      >
        Back to Registrations
      </Button>

      {/* ── Header row: Title + Edit ── */}
      <Group justify="space-between" mb="sm">
        <Title order={2}>Registration Details</Title>
        <Group gap={6}>
          <Tooltip label="Refresh">
            <ActionIcon
              variant="subtle"
              onClick={() => { fetchRegistration(); refetchSessionTabs(); }}
              loading={loading}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>

          <Button
            variant="filled"
            leftSection={<IconPencil size={16} />}
            onClick={() => setEditOpen(true)}
            disabled={!registration}
          >
            Edit
          </Button>

          <Menu shadow="md" width={220} position="bottom-end">
            <Menu.Target>
              <Button
                variant="default"
                rightSection={changingStatus ? <Loader size={14} /> : <IconChevronDown size={14} />}
                disabled={loadingStatuses || changingStatus}
              >
                {registration.statusID
                  ? statuses.find((s) => s.statusID === registration.statusID)?.name ?? 'Change status'
                  : 'Change status'}
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Change status</Menu.Label>

              {loadingStatuses ? (
                <Center py="sm">
                  <Loader size="xs" />
                </Center>
              ) : (
                statuses.map((s) => (
                  <Menu.Item
                    key={s.statusID}
                    onClick={() => handleStatusChange(String(s.statusID))}
                    disabled={s.statusID === registration.statusID}
                    leftSection={
                      s.color ? (
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: s.color,
                            flexShrink: 0,
                          }}
                        />
                      ) : null
                    }
                  >
                    {s.name ?? `Status ${s.statusID}`}
                  </Menu.Item>
                ))
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* ── Profile card ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Group align="flex-start" gap="md">
            <Avatar color={listItem?.courseColor ?? 'blue'} radius="md" size={80}>
              
              <IconBooks size={28} />
            </Avatar>

            <div>
              <Group gap="xs" align="center">
                <Title order={4}>{courseName}</Title>
                <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(statusColor)}>
                  {statusName}
                </Badge>
                {listItem?.locationName && (
                  <Badge
                    variant="filled"
                    styles={badgeStyles}
                    style={brandBadgeStyle(listItem.locationColor)}
                  >
                    {listItem.locationName}
                  </Badge>
                )}
              </Group>
              {registration.description && (
                <Text size="sm" c="dimmed">{registration.description}</Text>
              )}
              <Text size="sm" c="dimmed">
                {listItem?.periodDisplay }
              </Text>
              {/* ⚠️ Read off `listItem` (the RegistrationListView row), not `registration`. The page
                  holds the **entity** from `getById`, which carries neither of these; the list row
                  carries both. */}
              {listItem?.registeredDate && (
                <Text size="xs" c="dimmed">Registered {listItem.registeredDate}</Text>
              )}
            </div>
          </Group>

          <Group gap="xl">
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Participants</Text>
              <Group gap={4}>
                <IconUsers size={16} />
                <Text fw={600}>{listItem?.participantCount ?? 0}</Text>
              </Group>
            </Stack>
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Lead Facilitator</Text>
              <Text fw={600}>{listItem?.leadFacilitatorName ?? '—'}</Text>
            </Stack>
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Next Session</Text>
              <Text fw={600}>{listItem?.nextSessionDisplay ?? '—'}</Text>
            </Stack>
          </Group>
        </Group>

        {typeof listItem?.progressPercent === 'number' && (
          <>
            <Divider my="sm" />
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">Progress</Text>
              <Text size="xs" c="dimmed">{listItem.progressPercent}%</Text>
            </Group>
            <Progress
              value={listItem.progressPercent}
              color={listItem.isBehindSchedule ? 'red' : 'blue'}
              radius="xl"
            />
          </>
        )}
      </Card>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onChange={(v) => setActiveTab((v as TabKey) ?? 'members')} keepMounted={false}>
        {/* No `mb` here: the panel card below carries `mt="sm"`, matching the leader and
            facilitator detail pages. Both together double the gap. */}
        <Tabs.List>

          <Tabs.Tab value="members" leftSection={<IconUsers size={16} />} style={tabStyle(activeTab === 'members')}>Members</Tabs.Tab>
          <Tabs.Tab value="facilitators" leftSection={<IconUserCheck size={16} />} style={tabStyle(activeTab === 'facilitators')}>Facilitators</Tabs.Tab>

          {/* ── Dynamic session tabs: one per special session, plus one combined "Sessions" tab ── */}
          {sessionTabsLoading && sessionTabs.length === 0 && (
            <Center px="md">
              <Loader size="xs" />
            </Center>
          )}
          {sessionTabs.map((tab) => (
            <Tabs.Tab
              key={tab.key}
              value={tab.key}
              leftSection={tab.kind === 'special' ? <IconClipboardCheck size={16} /> : <IconCalendar size={16} />}
              style={tabStyle(activeTab === tab.key)}
            >
              {tab.title}
            </Tabs.Tab>
          ))}

          <Tabs.Tab value="additionalDetails" leftSection={<IconMessageQuestion size={16} />} style={tabStyle(activeTab === 'additionalDetails')}>Additional Details</Tabs.Tab>
          {/* conditional render for children tab */}
          {childrenRow && (
            <Tabs.Tab value="children" leftSection={<IconBabyCarriage size={16} />} style={tabStyle(activeTab === 'children')}>
              Children
            </Tabs.Tab>
          )}
          <Tabs.Tab value="notes" leftSection={<IconNotes size={16} />} style={tabStyle(activeTab === 'notes')}>Notes</Tabs.Tab>
          <Tabs.Tab value="courseFiles" leftSection={<IconBooks size={16} />} style={tabStyle(activeTab === 'courseFiles')}>Course Files</Tabs.Tab>
        </Tabs.List>

        <Card withBorder radius="md" padding="lg" mt="sm">

          <Tabs.Panel value="members">
            <MembersTab
              registrationId={registrationID}
              active={activeTab === 'members'}
              onRegistrationChange={fetchRegistration}
              intakeID={registration?.intakeID}
            />
          </Tabs.Panel>
          <Tabs.Panel value="facilitators">
            <FacilitatorsTab
              registrationId={registrationID}
              active={activeTab === 'facilitators'}
              onRegistrationChange={fetchRegistration}
            />
          </Tabs.Panel>

          {/* ── Dynamic session tab panels ── */}
          {sessionTabs.map((tab) => (
            <Tabs.Panel key={tab.key} value={tab.key}>
              {tab.kind === 'special' ? (
                <SpecialSessionsTabContent
                  registrationID={registrationID}
                  courseSessionID={Number(tab.session.courseSessionID)}
                  registrationSessionID={tab.session.registrationSessionID}
                />
              ) : (
                <Stack gap="md">
                  <Group justify="flex-end" gap={4}>
                    <Tooltip label="Refresh sessions" withArrow>
                      <ActionIcon
                        variant="subtle"
                        onClick={refetchSessionTabs}
                        loading={sessionTabsLoading}
                        size="lg"
                      >
                        <IconRefresh size={16} />
                      </ActionIcon>
                    </Tooltip>

                    {/* ── Add Session dropdown ── ported from old SessionsTab.tsx.
                        Lists course sessions not yet added to this registration. */}
                    <Menu shadow="md" width={320} position="bottom-end">
                      <Menu.Target>
                        <Indicator
                          label={availableCourseSessions.length}
                          size={16}
                          disabled={availableCourseSessions.length === 0}
                          color="red"
                          offset={4}
                        >
                          <Button
                            leftSection={<IconPlus size={16} />}
                            rightSection={<IconChevronDown size={14} />}
                          >
                            Add Session
                          </Button>
                        </Indicator>
                      </Menu.Target>

                      <Menu.Dropdown>
                        <Menu.Label>Available course sessions</Menu.Label>

                        {courseSessionsLoading ? (
                          <Center py="sm">
                            <Loader size="xs" />
                          </Center>
                        ) : availableCourseSessions.length === 0 ? (
                          <Text size="xs" c="dimmed" px="sm" py="xs">
                            No unadded active sessions available on this course.
                          </Text>
                        ) : (
                          <ScrollArea.Autosize mah={300}>
                            {availableCourseSessions.map((cs) => (
                              <Menu.Item
                                key={cs.courseSessionID}
                                onClick={() => handleAddFromCourseSession(cs)}
                                disabled={addingSessionID === cs.courseSessionID}
                                leftSection={
                                  cs.color ? (
                                    <div
                                      style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: cs.color,
                                        flexShrink: 0,
                                      }}
                                    />
                                  ) : null
                                }
                                rightSection={
                                  addingSessionID === cs.courseSessionID ? (
                                    <Loader size={12} />
                                  ) : cs.isSpecial ? (
                                    <Badge size="xs" variant="light" color="grape">
                                      Special
                                    </Badge>
                                  ) : null
                                }
                              >
                                {cs.displayName ?? cs.name}
                              </Menu.Item>
                            ))}
                          </ScrollArea.Autosize>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <Stack gap={0}>
                    <Group justify="space-between" px="sm" pb={6} align="center">
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="xs" c="dimmed" fw={500} tt="uppercase">Session</Text>
                      </Box>
                      <Group gap="xs" align="center">
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 130, textAlign: 'left' }}>Date</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Progress</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Status</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Actions</Text>
                      </Group>
                    </Group>
                    <Divider mb={4} />
                  {allSessions.map((s) => {
                    return (
                      <Group
                        key={s.registrationSessionID}
                        justify="space-between"
                        align="flex-start"
                        py="sm"
                        px="sm"
                        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                      >   
                        <Stack gap={2}>
                          <Group gap={6}>
                            <Text size="sm" fw={500}>{s.name ?? '—'}</Text>
                            {s.isSpecial && (
                              <Badge size="xs" variant="light" color="grape">Special</Badge>
                            )}
                            {s.isToday && (
                              <Badge size="xs" variant="filled" color="blue">Today</Badge>
                            )}
                            {s.isOverdue && (
                              <Badge size="xs" variant="filled" color="red">Overdue</Badge>
                            )}
                          </Group>
                          {s.description && (
                            <Text size="xs" c="dimmed" lineClamp={1}>{s.description}</Text>
                          )}
                          {s.templateName && s.templateName !== s.name && (
                            <Text size="xs" c="dimmed">Template: {s.templateName}</Text>
                          )}
                        </Stack>

                        <Group gap="xs" align="center">
                          <Group gap={4} style={{ minWidth: 130 }}>
                            <IconCalendar size={14} style={{ opacity: 0.6 }} />
                            <Text size="xs" c="dimmed">{s.sessionDateDisplay ?? '—'}</Text>
                          </Group>

                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <SessionStateMenu session={s} kind="progress" onUpdated={handleSessionUpdate} />
                          </Box>

                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <SessionStateMenu session={s} kind="status" onUpdated={handleSessionUpdate} />
                          </Box>
                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <Group gap={4} justify="center" wrap="nowrap">
                              <Tooltip label="View session">
                                <ActionIcon
                                  variant="light"
                                  color="blue"
                                  onClick={() => openSessionDetails(s)}
                                >
                                  <IconEye size={16} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Edit session">
                                <ActionIcon
                                  variant="light"
                                  color="gray"
                                  onClick={() => openSessionEdit(s)}
                                >
                                  <IconPencil size={16} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Box>
                        </Group>
                      </Group>
                    );
                  })}
                  </Stack>
                </Stack>
              )}
            </Tabs.Panel>
          ))}

          <Tabs.Panel value="additionalDetails">
            <AdditionalDetailsTab
              registrationId={registrationID}
              active={activeTab === 'additionalDetails'}
            />
          </Tabs.Panel>
          {childrenRow && (
            <Tabs.Panel value="children">
              <ChildrenTab
                registrationId={registrationID}
                active={activeTab === 'children'}
                row={childrenRow}
                onChanged={fetchChildrenData}
              />
            </Tabs.Panel>
          )}
          <Tabs.Panel value="notes">
            <NotesTab
              registrationId={registrationID}
              active={activeTab === 'notes'}
              currentUserId={user?.id ?? undefined}
            />
          </Tabs.Panel>
          <Tabs.Panel value="courseFiles">
            <CourseFilesTab
              registrationId={registrationID}
              active={activeTab === 'courseFiles'}
              onRegistrationChange={fetchRegistration}
            />
          </Tabs.Panel>
        </Card>
      </Tabs>
       
        {/* ── Session Details Modal ── */}
          {detailsSession && (
            <RegistrationSessionDetailsModal
              opened={detailsOpen}
              onClose={() => {
                setDetailsOpen(false);
                setDetailsSession(null);
              }}
              session={detailsSession}
              currentUserId={user?.id ?? undefined}
              onEdit={() => {
                setDetailsOpen(false);
                openSessionEdit(detailsSession);
              }}
              onSessionChange={handleSessionUpdate}
            />
          )}

          <EditSessionDrawer
            opened={editSessionOpen}
            onClose={() => {
              setEditSessionOpen(false);
              setEditingSession(null);
            }}
            session={editingSession}
            onSaved={refetchSessionTabs}
          />

      {/* ── Edit Drawer ── */}
      <Drawer
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        title={
          <Group gap="xs">
            <IconPencil size={18} />
            <Text fw={600}>Edit Registration</Text>
          </Group>
        }
        position="right"
        size="md"
        padding="md"
      >
        {registration && (
          <EditTab
            registration={registration}
            onSaved={() => { fetchRegistration(); setEditOpen(false); }}
          />
        )}
      </Drawer>
    </Container>
  );
}